import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Box, Flex, Icon, Progress, SimpleGrid, Text, VStack} from "@chakra-ui/react";
import {FaBell, FaCheckCircle, FaMusic} from "react-icons/fa";
import {BELL_INFO_UPDATED_EVENT, readBellInfo} from "../../../../utils/BellStorageUtils";

const isPast = (hourTime) => {
    if (!hourTime) return false;
    const [time, modifier] = hourTime.trim().split(" ");
    let [hours, minutes] = (time || "").split(":").map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) return false;
    if (modifier?.toUpperCase() === "PM" && hours !== 12) hours += 12;
    if (modifier?.toUpperCase() === "AM" && hours === 12) hours = 0;
    const d = new Date();
    d.setHours(hours, minutes, 0, 0);
    return new Date() > d;
};

const StatCard = ({icon, iconColor, iconBg, label, value, children, accent}) => (
    <Box
        bg="white"
        borderRadius="12px"
        borderWidth="1px"
        borderColor="gray.100"
        boxShadow="0 6px 18px rgba(0,0,0,0.05)"
        p={{base: 2, md: 2.5}}
        h="100%"
        _dark={{bg: "gray.800", borderColor: "gray.700"}}
    >
        <Flex justify="space-between" align="flex-start">
            <Flex align="center" justify="center" w="30px" h="30px" borderRadius="9px" bg={iconBg}>
                <Icon as={icon} boxSize={3.5} color={iconColor}/>
            </Flex>
            {accent}
        </Flex>
        <Text mt={2} fontSize={{base: "lg", md: "xl"}} fontWeight="800" lineHeight="1">
            {value}
        </Text>
        <Text mt={0.5} fontSize="xs" color="gray.500">
            {label}
        </Text>
        {children}
    </Box>
);

const BellStatCards = () => {
    const [bells, setBells] = useState(() => readBellInfo());
    const refresh = useCallback(() => setBells(readBellInfo()), []);

    useEffect(() => {
        const onStorage = (e) => {
            if (!e.key || e.key === "bellInfo") refresh();
        };
        window.addEventListener(BELL_INFO_UPDATED_EVENT, refresh);
        window.addEventListener("storage", onStorage);
        return () => {
            window.removeEventListener(BELL_INFO_UPDATED_EVENT, refresh);
            window.removeEventListener("storage", onStorage);
        };
    }, [refresh]);

    const stats = useMemo(() => {
        const list = bells || [];
        const total = list.length;
        const rung = list.filter((b) => isPast(b.hourTime)).length;
        const music = list.filter((b) => b.type === "MUSIC" || b.type === "NATIONAL_ANTHEM").length;
        const completion = total ? Math.round((rung / total) * 100) : 0;
        return {total, rung, music, completion};
    }, [bells]);

    return (
        <VStack spacing={{base: 3, md: 4}} align="stretch">
            {/* Compact "bells completed" card — ~half the height of a StatCard: a
                single-row layout (icon + value + inline progress) instead of the
                stacked big-number version. */}
            <Box
                bg="white"
                borderRadius="16px"
                borderWidth="1px"
                borderColor="gray.100"
                boxShadow="0 6px 18px rgba(0,0,0,0.05)"
                px={{base: 3, md: 4}}
                py={{base: 2.5, md: 3}}
                _dark={{bg: "gray.800", borderColor: "gray.700"}}
            >
                <Flex align="center" gap={3}>
                    <Flex align="center" justify="center" w="36px" h="36px" borderRadius="10px" bg="green.400" flexShrink={0}>
                        <Icon as={FaCheckCircle} boxSize={4} color="white"/>
                    </Flex>
                    <Box flex="1" minW="0">
                        <Flex align="baseline" justify="space-between" gap={2}>
                            <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                Today's bells completed
                            </Text>
                            <Text fontSize="lg" fontWeight="800" lineHeight="1">
                                {stats.completion}%
                            </Text>
                        </Flex>
                        <Progress
                            mt={1.5}
                            value={stats.completion}
                            size="xs"
                            colorScheme="green"
                            borderRadius="full"
                        />
                        <Text mt={1} fontSize="0.7rem" color="gray.500">
                            {stats.rung} of {stats.total} rung
                        </Text>
                    </Box>
                </Flex>
            </Box>

            <SimpleGrid columns={2} spacing={{base: 3, md: 4}}>
                <StatCard
                    icon={FaBell}
                    iconColor="blue.500"
                    iconBg="blue.50"
                    label="Total bells scheduled"
                    value={stats.total}
                />
                <StatCard
                    icon={FaMusic}
                    iconColor="purple.500"
                    iconBg="purple.50"
                    label="Music & anthems"
                    value={stats.music}
                />
            </SimpleGrid>
        </VStack>
    );
};

export default BellStatCards;
